import { CSSProperties, ChangeEvent, FC, HTMLProps, MutableRefObject, ReactElement, ReactNode } from 'react';

import { Tooltip, TooltipPositions } from '@pp/core/src/components/tooltip/Tooltip';
import { useMedia } from '@pp/core/src/hooks/useMedia';
import { useModifiers } from '@pp/core/src/styles/hooks/useModifiers';

import styles from './betInput.module.css';

export interface InputProps
  extends Omit<HTMLProps<HTMLInputElement>, 'prefix' | 'onSubmit' | 'value' | 'postfix' | 'label'> {
  inputRef?: MutableRefObject<any>;
  value?: string | number;
  placeholder?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  prefix?: ReactNode;
  postfix?: ReactNode;
  label?: ReactNode;
  containerClass?: string;
  isDisabled?: boolean;
  tooltipMsg?: string;
  isEditing?: boolean;
  forceTriggerTooltip?: boolean;
  inputCSSOverride?: string;
  customInputFontSize?: number;
  attrs?: string[];
}

export const BetInput: FC<InputProps> = ({
  inputRef,
  value,
  placeholder,
  onChange,
  prefix,
  postfix,
  label,
  isDisabled,
  containerClass,
  type = 'text',
  tooltipMsg = '',
  forceTriggerTooltip = false,
  inputCSSOverride,
  customInputFontSize = 0,
  attrs,
}): ReactElement => {
  const mods = useModifiers(styles, 'bet_input', {
    disabled: isDisabled,
  });
  const isWide = useMedia('(min-width: 1024px)');

  return (
    <div className={`${styles.input_container} ${mods} ${containerClass}`}>
      {prefix && <div className={styles.input__prefix}>{prefix}</div>}
      <Tooltip
        position={TooltipPositions.Top}
        text={tooltipMsg}
        disabled={!tooltipMsg}
        displayTrigger={forceTriggerTooltip}
        displayTimer={2000}
        attrs={attrs}
        displayOnTouch={true}
      >
        <div className={styles.bet_input_block}>
          {label && <div className={styles.bet_input_label}>{label}</div>}
          <input
            ref={inputRef}
            className={`${styles.bet_input} ${inputCSSOverride} ${mods}`}
            style={
              customInputFontSize
                ? ({
                  '--input-text-size': `${customInputFontSize}px`,
                } as CSSProperties)
                : ({} as CSSProperties)
            }
            type={type}
            value={value}
            placeholder={placeholder}
            onChange={onChange}
            disabled={isDisabled}
            readOnly={!isWide ? true : false}
          />
        </div>
      </Tooltip>
      {postfix && <div className={styles.input_postfix}>{postfix}</div>}
    </div>
  );
};
